<div class="mb-4">
  <p-accordion
    [multiple]="false"
    [activeIndex]="isExpanded ? 0 : -1"
    (onClose)="isExpanded = false"
    (onOpen)="isExpanded = true"
  >
    <p-accordion-panel value="0">
      <p-accordion-header>
        <div class="flex items-center gap-2">
          <i class="pi pi-filter"></i>
          <span>{{ 'filters.title' | translate }}</span>
          @if (hasActiveFilters()) {
            <span class="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-white">{{ countActiveFilters() }}</span>
          }
        </div>
      </p-accordion-header>
      <p-accordion-content>
        <form
          [formGroup]="filterForm"
          class="flex flex-col gap-4"
        >
          <!-- Guest Name Filter -->
          <div class="max-w-screen-lg">
            <h5 class="mb-2 text-base font-medium">{{ 'filters.guestName' | translate }}</h5>
            <div>
              <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                <p-inputGroupAddon>
                  <i class="pi pi-search"></i>
                </p-inputGroupAddon>
                <input
                  id="guestName"
                  type="text"
                  pInputText
                  class="w-full"
                  formControlName="guestName"
                  [placeholder]="'filters.searchPlaceholder' | translate"
                />
                @if (filterForm.get('guestName')?.value) {
                  <p-inputGroupAddon>
                    <button
                      type="button"
                      class="p-inputgroup-addon cursor-pointer"
                      (click)="clearFilterField('guestName')"
                    >
                      <i class="pi pi-times"></i>
                    </button>
                  </p-inputGroupAddon>
                }
              </p-inputGroup>
            </div>
          </div>

          <!-- Status Filter -->
          <div class="max-w-screen-lg">
            <h5 class="mb-2 text-base font-medium">{{ 'filters.status' | translate }}</h5>
            <div class="w-full sm:w-2/3 lg:w-1/3">
              @if (bookingStatusLoading) {
                <div class="flex items-center gap-2 p-2">
                  <i class="pi pi-spin pi-spinner text-primary"></i>
                  <span>{{ 'common.loading' | translate }}</span>
                </div>
              } @else {
                <p-select
                  id="status"
                  [options]="(bookingStatusItems$ | async) || []"
                  formControlName="status"
                  [placeholder]="'filters.selectStatus' | translate"
                  [showClear]="true"
                  styleClass="w-full"
                  optionValue="model.id"
                  appendTo="body"
                >
                  <ng-template
                    pTemplate="selectedItem"
                    let-item
                  >
                    @if (item?.model && getStatusBadge(item.model.id)) {
                      <app-badge-info [badge]="getStatusBadge(item.model.id)!"></app-badge-info>
                    } @else {
                      {{ item?.localizableTitle | translate }}
                    }
                  </ng-template>
                  <ng-template
                    pTemplate="item"
                    let-option
                  >
                    @if (option?.model && getStatusBadge(option.model.id)) {
                      <app-badge-info [badge]="getStatusBadge(option.model.id)!"></app-badge-info>
                    } @else {
                      {{ option?.localizableTitle | translate }}
                    }
                  </ng-template>
                </p-select>
              }
            </div>
          </div>

          <!-- Check-in Date Filter -->
          <div class="max-w-screen-lg">
            <h5 class="mb-2 text-base font-medium">{{ 'filters.bookingStartDate' | translate }}</h5>
            <div
              class="flex flex-col gap-3 sm:flex-row"
              formGroupName="startDate"
            >
              <div class="w-full sm:w-1/3 lg:w-1/4">
                <p-select
                  formControlName="operator"
                  [options]="dateOperators"
                  optionLabel="label"
                  optionValue="value"
                  styleClass="w-full"
                  appendTo="body"
                >
                  <ng-template
                    pTemplate="selectedItem"
                    let-item
                  >
                    {{ item.label | translate }}
                  </ng-template>
                  <ng-template
                    pTemplate="item"
                    let-option
                  >
                    {{ option.label | translate }}
                  </ng-template>
                </p-select>
              </div>
              <div class="w-full sm:w-2/3 lg:w-1/3">
                <p-datepicker
                  formControlName="date"
                  styleClass="w-full"
                  [showIcon]="true"
                  [dateFormat]="dateFormat"
                  [placeholder]="'filters.datePlaceholder' | translate"
                  [showButtonBar]="true"
                  appendTo="body"
                >
                </p-datepicker>
              </div>
            </div>
          </div>

          <!-- Check-out Date Filter -->
          <div class="max-w-screen-lg">
            <h5 class="mb-2 text-base font-medium">{{ 'filters.bookingEndDate' | translate }}</h5>
            <div
              class="flex flex-col gap-3 sm:flex-row"
              formGroupName="endDate"
            >
              <div class="w-full sm:w-1/3 lg:w-1/4">
                <p-select
                  formControlName="operator"
                  [options]="dateOperators"
                  optionLabel="label"
                  optionValue="value"
                  styleClass="w-full"
                  appendTo="body"
                >
                  <ng-template
                    pTemplate="selectedItem"
                    let-item
                  >
                    {{ item.label | translate }}
                  </ng-template>
                  <ng-template
                    pTemplate="item"
                    let-option
                  >
                    {{ option.label | translate }}
                  </ng-template>
                </p-select>
              </div>
              <div class="w-full sm:w-2/3 lg:w-1/3">
                <p-datepicker
                  formControlName="date"
                  styleClass="w-full"
                  [showIcon]="true"
                  [dateFormat]="dateFormat"
                  [placeholder]="'filters.datePlaceholder' | translate"
                  [showButtonBar]="true"
                  appendTo="body"
                >
                </p-datepicker>
              </div>
            </div>
          </div>

          <!-- Filter Actions -->
          <div class="flex justify-end">
            <div class="flex gap-3">
              <button
                pButton
                type="button"
                [label]="'common.reset' | translate"
                class="p-button-outlined"
                (click)="resetAllFilters()"
              ></button>

              <button
                pButton
                type="button"
                [label]="'common.apply' | translate"
                class="p-button-primary"
                (click)="applyFilters()"
              ></button>
            </div>
          </div>
        </form>
      </p-accordion-content>
    </p-accordion-panel>
  </p-accordion>
</div>
